import { SubscriptionDetailsResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import FunnelKey from "src/constants/funnelKey";
import { determineFunnelKey } from "src/lib/determineFunnelKey";
import { logSentryEvent } from "src/lib/logSentryEvent";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import { subscriptionState } from "src/state/state";
import { onUserSessionReset } from "src/lib/onUserSessionReset";
import { clearSamlFunnelKey } from "src/lib/useSamlAuth";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "../UserPreferencesCubit/UserPreferencesCubit";

export enum AppAccessStatus {
  LOADING = "LOADING",
  GRANTED = "GRANTED",
  NEEDS_FUNNEL = "NEEDS_FUNNEL",
  UNKNOWN = "UNKNOWN"
}

export interface AppAccessState {
  status: AppAccessStatus;
  funnelRedirectPath?: string;
  funnelKey?: FunnelKey;
}

const ACCESS_GRANTING_STATUSES = [
  SubscriptionDetailsResponse.status.ACTIVE,
  SubscriptionDetailsResponse.status.FINISHED,
  SubscriptionDetailsResponse.status.PAUSED,
  SubscriptionDetailsResponse.status.IN_REVIEW
];

const HANDOVER_TOKEN_PARAMS = ["token", "lookupKey"];

export default class AppAccessCubit extends Cubit<AppAccessState> {
  static keepAlive = true;
  constructor() {
    super({
      status: AppAccessStatus.LOADING
    });

    // A decision made for the previous user must never survive into the next
    // one's session: a stale NEEDS_FUNNEL sends a paying member back into
    // signup, a stale GRANTED lets an unfinished signup into the app.
    onUserSessionReset(() => {
      clearSamlFunnelKey();
      this.reset();
    });
  }

  private grantedFromCache = false;

  /**
   * Optimistic boot state for a returning member: the persisted access flag
   * renders the app shell before any network call. Only a successful
   * revalidation may move this member on to NEEDS_FUNNEL.
   */
  seedGrantedFromCache(): void {
    if (this.state.status !== AppAccessStatus.LOADING) {
      return;
    }

    this.grantedFromCache = true;
    this.emit({
      status: AppAccessStatus.GRANTED,
      funnelRedirectPath: undefined,
      funnelKey: undefined
    });
  }

  private grantAccess(): void {
    StorageController.setHasAppAccess(true);
    this.emit({
      status: AppAccessStatus.GRANTED,
      funnelRedirectPath: undefined,
      funnelKey: undefined
    });
  }

  // subscriptionsLoaded/profileLoaded distinguish "confirmed no subscriptions"
  // from "the data hasn't loaded yet" so callers mid-fetch don't get funneled
  // into signup while their status is still unknown.
  async evaluateAccess(
    subscriptionsLoaded = true,
    profileLoaded = true
  ): Promise<void> {
    if (!subscriptionsLoaded || !profileLoaded) {
      // A failed or timed out revalidation must never lock a returning member
      // out of the app, so the cached grant stands.
      if (
        this.grantedFromCache &&
        this.state.status === AppAccessStatus.GRANTED
      ) {
        logSentryEvent(
          "App access revalidation failed, keeping cached access",
          { subscriptionsLoaded, profileLoaded },
          "warn"
        );
        return;
      }

      this.emit({
        status: AppAccessStatus.UNKNOWN,
        funnelRedirectPath: undefined,
        funnelKey: undefined
      });
      return;
    }

    const subscriptions = subscriptionState.state.allSubscriptions ?? [];

    const hasAccessGrantingSubscription = subscriptions.some((s) =>
      ACCESS_GRANTING_STATUSES.includes(s.status)
    );
    if (hasAccessGrantingSubscription) {
      this.grantAccess();
      return;
    }

    const draftSubscription = subscriptions.find(
      (s) => s.status === SubscriptionDetailsResponse.status.DRAFT
    );

    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    const funnelKey = determineFunnelKey({
      draftSubscription,
      lastSignupFunnel:
        userPreferences.state[UserPreferenceKeys.lastSignupFunnel]
    });
    const params = new URLSearchParams(window.location.search);
    // Evaluated while still on /auth/ott, so drop the spent handover token
    // instead of carrying it into the funnel URL.
    HANDOVER_TOKEN_PARAMS.forEach((key) => params.delete(key));
    const queryString = params.toString();
    const funnelRedirectPath = `/signup/${funnelKey}${queryString ? `?${queryString}` : ""}`;

    this.emit({
      status: AppAccessStatus.NEEDS_FUNNEL,
      funnelRedirectPath,
      funnelKey
    });
  }

  reset(): void {
    this.grantedFromCache = false;
    this.emit({
      status: AppAccessStatus.LOADING,
      funnelRedirectPath: undefined,
      funnelKey: undefined
    });
  }
}
